import * as React from 'react';
import { differenceInMinutes, format, getHours, getMinutes } from 'date-fns';
import { CalendarEvent } from 'hooks/useCalendarEvents';
import {
  CombinedInterval,
  EventsByStartDate,
  Lesson,
  UnifiedCalendarEvent,
} from './props';

export const MAX_VISIBLE_LANES = 2;

export const useGroupEventsByStartDate = (
  lessons: Lesson[],
  externalEvents: CalendarEvent[],
): EventsByStartDate => {
  return React.useMemo(() => {
    const grouped: EventsByStartDate = {};

    const pushEvent = (event: UnifiedCalendarEvent) => {
      if (!grouped[event.date]) {
        grouped[event.date] = { events: [], intervals: [] };
      }
      grouped[event.date].events.push(event);
    };

    lessons.forEach((lesson) => {
      const start = new Date(lesson.startDateTime);
      const end = new Date(lesson.endDateTime);
      const date = format(start, 'yyyy-MM-dd');

      pushEvent({
        type: 'bounce',
        id: lesson.id,
        title: lesson.title,
        date,
        startTimestamp: start.getTime(),
        endTimestamp: end.getTime(),
        startHour: getHours(start),
        startMinute: getMinutes(start),
        durationMinutes: differenceInMinutes(end, start),
        numberInInterval: 1,
        intervalVisibleCount: 1,
        indexOfInterval: 0,
        isOverflow: false,
        lesson: {
          ...lesson,
          date,
          startTimestamp: start.getTime(),
          endTimestamp: end.getTime(),
          startHour: getHours(start),
          startMinute: getMinutes(start),
          endHour: getHours(end),
          endMinute: getMinutes(end),
          durationMinutes: differenceInMinutes(end, start),
          numberInInterval: 1,
          indexOfInterval: 0,
        },
      });
    });

    // Bounce stamps the Google events it creates with
    // `extendedProperties.private.source = 'bounce'` and the originating
    // `lessonId` (see /api/v1/google/create-event). Suppress those external
    // events when their Bounce lesson is already in view, so the slot doesn't
    // render the same lesson twice. We only suppress when the lesson is
    // actually in `lessons` — that way a stale Google event from a deleted
    // Bounce lesson still surfaces as a normal conflict.
    const bounceLessonIds = new Set(lessons.map((l) => l.id));

    externalEvents.forEach((event) => {
      if (!event.start?.dateTime || !event.end?.dateTime || !event.id) return;
      const extendedLessonId = event.extendedProperties?.private?.lessonId;
      if (
        event.extendedProperties?.private?.source === 'bounce' &&
        extendedLessonId &&
        bounceLessonIds.has(extendedLessonId)
      ) {
        return;
      }
      const start = new Date(event.start.dateTime);
      const end = new Date(event.end.dateTime);
      const date = format(start, 'yyyy-MM-dd');

      pushEvent({
        type: 'external',
        id: event.id,
        title: event.summary || '(no title)',
        date,
        startTimestamp: start.getTime(),
        endTimestamp: end.getTime(),
        startHour: getHours(start),
        startMinute: getMinutes(start),
        durationMinutes: differenceInMinutes(end, start),
        numberInInterval: 1,
        intervalVisibleCount: 1,
        indexOfInterval: 0,
        isOverflow: false,
        event,
      });
    });

    Object.keys(grouped).forEach((date) => {
      const group = grouped[date];

      // Sort by start time; for ties, Bounce events come first so they get
      // the left-most lane(s) within an overlap interval.
      group.events.sort((a, b) => {
        if (a.startTimestamp !== b.startTimestamp) {
          return a.startTimestamp - b.startTimestamp;
        }
        if (a.type === 'bounce' && b.type !== 'bounce') return -1;
        if (a.type !== 'bounce' && b.type === 'bounce') return 1;
        return 0;
      });

      // Merge overlapping events into intervals.
      const eventsByInterval: UnifiedCalendarEvent[][] = [];

      group.events.forEach((event) => {
        const last = group.intervals[group.intervals.length - 1];
        const isOverlapping = last != null && event.startTimestamp < last.end;

        if (isOverlapping && last) {
          last.end = Math.max(event.endTimestamp, last.end);
          last.totalCount += 1;
          event.indexOfInterval = group.intervals.length - 1;
          eventsByInterval[group.intervals.length - 1].push(event);
        } else {
          group.intervals.push({
            start: event.startTimestamp,
            end: event.endTimestamp,
            totalCount: 1,
            visibleCount: 0,
            overflowCount: 0,
          });
          event.indexOfInterval = group.intervals.length - 1;
          eventsByInterval.push([event]);
        }
      });

      // Assign lanes with Bounce priority: Bounce events always render,
      // external events fill remaining slots up to MAX_VISIBLE_LANES, the
      // rest become overflow and surface via a "+N more" badge.
      eventsByInterval.forEach((intervalEvents, intervalIdx) => {
        const interval = group.intervals[intervalIdx] as CombinedInterval;
        const bounceCount = intervalEvents.filter((e) => e.type === 'bounce').length;
        let externalSlotsRemaining = Math.max(0, MAX_VISIBLE_LANES - bounceCount);
        let lane = 0;

        intervalEvents.forEach((event) => {
          if (event.type === 'bounce') {
            lane += 1;
            event.numberInInterval = lane;
            event.isOverflow = false;
          } else if (externalSlotsRemaining > 0) {
            lane += 1;
            externalSlotsRemaining -= 1;
            event.numberInInterval = lane;
            event.isOverflow = false;
          } else {
            event.numberInInterval = 0;
            event.isOverflow = true;
          }
        });

        interval.visibleCount = lane;
        interval.overflowCount = intervalEvents.length - lane;

        intervalEvents.forEach((event) => {
          event.intervalVisibleCount = lane;
        });
      });
    });

    return grouped;
  }, [lessons, externalEvents]);
};
